import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from '@/providers/RouterShim';
import useBranch from '../../../../data/branch/useBranch';
import useBranches from '../../../../data/branch/useBranches';
import usePermissions from '../../../../data/permissions/usePermissions';
import usePermissionWebsite from '../../../../data/permissions/usePermissionWebsite';
import useWorkOrderPlanTypes from '../../../../data/pf/useWorkOrderPlanTypes';
import useSite from '../../../../data/websites/useSite';
import type { SplashListLinkProps } from '../../../../styled-components/components/SplashList';
import type { PageHeaderBreadcrumbsProps } from '../../../../styled-components/structures/PageHeader';
import {
  getBreadcrumbs,
  getNavigationMenu,
  getNavigationTree,
  getQuickNavigation,
  type NavigationPage,
  useResolvedPermissions,
} from './utils';

interface MainNavigationContextProps {
  initialized: boolean;
  navigationTree: NavigationPage[];
  quickNavigationLinks: NavigationPage[];
  menuLinks?: NavigationPage[];
  activePage: NavigationPage;
  breadcrumbs: PageHeaderBreadcrumbsProps[];
  landingPageLinks?: SplashListLinkProps[];
  expanded: boolean;
  mobileMenu: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggleMobileMenu: () => void;
}

const MainNavigationContext = createContext<MainNavigationContextProps | undefined>(undefined);

interface MainNavigationProviderProps {
  children: React.ReactNode;
}

export const MainNavigationProvider = ({ children }: MainNavigationProviderProps) => {
  const { branchId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [branch, requestBranch] = useBranch();
  const [_, requestBranches] = useBranches();
  const [__, requestPermissions] = usePermissions();
  const [___, requestWebsite] = useSite();
  const [websitePermission, requestWebsitePermission] = usePermissionWebsite();
  const resolvedPermissions = useResolvedPermissions();
  const [workOrderPlanTypes, requestWorkOrderPlanTypes] = useWorkOrderPlanTypes();

  const initialized =
    requestBranches.isSuccess &&
    requestPermissions.isSuccess &&
    requestWorkOrderPlanTypes.isSuccess &&
    requestBranch.isSuccess &&
    (requestWebsitePermission.isSuccess && websitePermission?.enabled
      ? requestWebsite.isSuccess || requestWebsite.isError
      : true);

  const [expanded, setExpanded] = useState(true);
  const onOpen = useCallback(() => setExpanded(true), []);
  const onClose = useCallback(() => setExpanded(false), []);

  const [mobileMenu, setMobileMenu] = useState(false);
  const onToggleMobileMenu = useCallback(() => setMobileMenu((s) => !s), []);

  // replace route when unauthorized or not available
  useEffect(() => {
    if (initialized) {
      if (pathname.includes('/website')) {
        if (!resolvedPermissions.website) {
          navigate(`/${branchId}`, { replace: true });
        } else if (!resolvedPermissions.websiteExist) {
          navigate(`/${branchId}/website`, { replace: true });
        }
      } else if (
        pathname.includes('/settings/process-settings/') &&
        !resolvedPermissions.processFlow
      ) {
        navigate(`/${branchId}/settings`, { replace: true });
      } else if (
        (pathname.startsWith(`/${branchId}/operations`) ||
          pathname.startsWith(`/${branchId}/settings/preparing`)) &&
        !resolvedPermissions.operations
      ) {
        navigate(`/${branchId}/settings`, { replace: true });
      } else if (branch) {
        if (branch.isParent) {
          if (
            pathname.startsWith(
              `/${branchId}/settings/retailer-information/logistic-appointment-templates`,
            )
          ) {
            navigate(`/${branchId}/settings/retailer-information`, { replace: true });
          } else if (
            pathname.startsWith(`/${branchId}/settings/preparing/stations`) ||
            pathname.startsWith(`/${branchId}/settings/preparing/roles`)
          ) {
            navigate(`/${branchId}/settings/preparing`, { replace: true });
          } else if (pathname.startsWith(`/${branchId}/leads`)) {
            navigate(`/${branchId}`, { replace: true });
          }
        } else {
          if (pathname.startsWith(`/${branchId}/settings/preparing/types`)) {
            navigate(`/${branchId}/settings/preparing`, { replace: true });
          }
        }
      }
    }
  }, [initialized, branchId]);

  const navigationTree = useMemo(
    () => getNavigationTree(branchId!, pathname, resolvedPermissions, branch, workOrderPlanTypes),
    [branchId, pathname, resolvedPermissions, branch, workOrderPlanTypes],
  );

  const quickNavigationLinks = useMemo(() => getQuickNavigation(navigationTree), [navigationTree]);

  const baseRoute = pathname.split('/').slice(0, 3).join('/');
  const menuLinks = useMemo(
    () => getNavigationMenu(baseRoute, navigationTree),
    [baseRoute, navigationTree],
  );

  const fullBreadcrumbs = useMemo(() => getBreadcrumbs(navigationTree), [navigationTree]);

  const breadcrumbs = useMemo(
    () =>
      fullBreadcrumbs.map((page) => ({
        displayName: page.title,
        link: { title: page.title, to: page.url },
      })),
    [fullBreadcrumbs],
  );

  const activePage = useMemo(
    () => fullBreadcrumbs[fullBreadcrumbs.length - 1] || {},
    [fullBreadcrumbs],
  );

  const landingPageLinks = useMemo(
    () =>
      activePage.pages?.map((page) => ({
        to: page.url,
        heading: page.title,
        description: page.description,
        icon: page.icon,
        externalUrl: page.externalUrl,
      })),
    [activePage],
  );

  return (
    <MainNavigationContext.Provider
      value={{
        initialized,
        navigationTree,
        quickNavigationLinks,
        menuLinks,
        breadcrumbs,
        activePage,
        landingPageLinks,
        expanded,
        mobileMenu,
        onOpen,
        onClose,
        onToggleMobileMenu,
      }}
    >
      {children}
    </MainNavigationContext.Provider>
  );
};

const useMainNavigation = (): MainNavigationContextProps => {
  const context = useContext(MainNavigationContext);

  if (!context) {
    throw new Error('useMainNavigation must be used within a MainNavigationContext');
  }

  return context;
};

export const useMobileMenuNavigation = () => {
  const { mobileMenu, onToggleMobileMenu } = useMainNavigation();
  return useMemo(
    () => ({
      mobileMenu,
      onToggleMobileMenu,
    }),
    [onToggleMobileMenu, mobileMenu],
  );
};

export const useNavigationTree = () => {
  const { navigationTree } = useMainNavigation();
  return useMemo(() => navigationTree, [navigationTree]);
};

export const useMainNavigationInitialized = () => {
  const { initialized } = useMainNavigation();
  return useMemo(() => initialized, [initialized]);
};

export const useQuickNavigationLinks = () => {
  const { quickNavigationLinks } = useMainNavigation();
  return useMemo(() => quickNavigationLinks, [quickNavigationLinks]);
};

export const useMenuLinks = () => {
  const { menuLinks } = useMainNavigation();
  return useMemo(() => menuLinks, [menuLinks]);
};

export const useBreadcrumbs = (includeLinkOfLast = false) => {
  const { breadcrumbs } = useMainNavigation();
  return useMemo(() => {
    const length = breadcrumbs.length;
    return [...breadcrumbs].map((b, i) => ({
      ...b,
      link: includeLinkOfLast || length - 1 !== i ? b.link : undefined,
    }));
  }, [breadcrumbs, includeLinkOfLast]);
};

export const useActivePage = () => {
  const { activePage } = useMainNavigation();
  return useMemo(() => activePage, [activePage]);
};

export const useLandingPageLinks = () => {
  const { landingPageLinks } = useMainNavigation();
  return useMemo(() => landingPageLinks, [landingPageLinks]);
};

export default useMainNavigation;
